import { usageColor } from "lib/usage-color";
import { useLanguage } from "context/languageContext";
import { translations } from "utils/i18n";

/** 412 -> "412 minutes", 1240 -> "1,240 minutes", null (still loading) -> "—" */
function formatMinutes(minutes, unit) {
  if (minutes == null) return "—";
  return `${Math.round(minutes).toLocaleString("en-GB")} ${unit}`;
}

/** Mirrors the avatar's ring (same color rule), just smaller — the header of the card below. */
function BalanceRing({ percentUsed, color }) {
  const radius = 8;
  const circumference = 2 * Math.PI * radius;
  const remainingPercent = 100 - (percentUsed ?? 0);

  return (
    <svg viewBox="0 0 20 20" className="size-4 shrink-0 -rotate-90" aria-hidden="true">
      <circle cx="10" cy="10" r={radius} fill="none" strokeWidth="2.5" className="stroke-border" />
      {percentUsed != null ? (
        <circle
          cx="10"
          cy="10"
          r={radius}
          fill="none"
          strokeWidth="2.5"
          strokeLinecap="round"
          stroke={color}
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - remainingPercent / 100)}
        />
      ) : null}
    </svg>
  );
}

/** The balance block at the top of the profile menu — ported from arkii-product's credit-balance.tsx. */
export function BalanceCard({ plan, isUnlimited, totalMinutes, remainingMinutes, percentUsed }) {
  const { language } = useLanguage();
  const tr = translations[language].sidebarProfile;
  const ringPercent = isUnlimited ? 0 : percentUsed;
  const color = isUnlimited ? "rgb(59,130,246)" : percentUsed != null ? usageColor(percentUsed) : null;

  return (
    // bg-secondary rather than bg-muted: bg-muted is the exact same var as
    // the dropdown's own bg-popover background, so the card needs a shade
    // that's actually distinct from what it's sitting on.
    <div className="rounded-md bg-secondary p-2">
      <div className="flex items-center gap-1.5 pb-1.5">
        <span className="flex items-center gap-1.5 text-xs font-medium text-foreground">
          <BalanceRing percentUsed={ringPercent} color={color} />
          {tr.balance}
        </span>
      </div>

      {plan ? (
        <dl className="grid gap-1 text-xs">
          <div className="flex items-baseline justify-between gap-2">
            <dt className="text-muted-foreground">{tr.total}</dt>
            <dd className="tabular-nums text-foreground">
              {isUnlimited ? tr.unlimited : formatMinutes(totalMinutes, tr.minutesUnit)}
            </dd>
          </div>
          {!isUnlimited ? (
            <div className="flex items-baseline justify-between gap-2">
              <dt className="text-muted-foreground">{tr.remaining}</dt>
              <dd className="tabular-nums text-foreground">{formatMinutes(remainingMinutes, tr.minutesUnit)}</dd>
            </div>
          ) : null}
        </dl>
      ) : (
        <p className="text-xs text-muted-foreground">{tr.noActivePlan}</p>
      )}
    </div>
  );
}
